<template>
  <div
    class="agent-chat-panel"
    :class="{ expanded: isExpanded }"
    :style="{ '--agent-color': store.selectedAgent?.color ?? '#11105E' }"
  >
    <!-- Header -->
    <div class="chat-header">
      <div class="chat-header-left">
        <div class="agent-avatar" :style="{ background: store.selectedAgent?.color }">
          <span class="material-icons">{{ store.selectedAgent?.icono }}</span>
        </div>
        <div>
          <div class="agent-name">{{ store.selectedAgent?.nombre }}</div>
          <div class="agent-desc">{{ store.selectedAgent?.descripcion }}</div>
        </div>
      </div>
      <div class="chat-header-actions">
        <span
          class="material-icons chat-action-btn"
          title="Capacidades del agente"
          @click="showInfo = true"
        >info_outline</span>
        <span
          class="material-icons chat-action-btn"
          :title="isExpanded ? 'Reducir' : 'Expandir'"
          @click="isExpanded = !isExpanded"
        >{{ isExpanded ? 'fullscreen_exit' : 'fullscreen' }}</span>
        <span
          class="material-icons chat-action-btn"
          title="Limpiar conversación"
          @click="store.clearHistory()"
        >delete_outline</span>
        <span
          class="material-icons chat-action-btn"
          title="Cambiar agente"
          @click="changeAgent"
        >swap_horiz</span>
        <span
          class="material-icons chat-action-btn"
          title="Cerrar"
          @click="store.clearAgent()"
        >close</span>
      </div>
    </div>

    <!-- Agent CV Modal -->
    <Teleport to="body">
      <div v-if="showInfo" class="agent-cv-overlay" @click.self="showInfo = false">
        <div class="agent-cv-modal">

          <!-- Modal Header -->
          <div class="cv-header" :style="{ background: store.selectedAgent?.color }">
            <div class="cv-header-left">
              <div class="cv-avatar">
                <span class="material-icons">{{ store.selectedAgent?.icono }}</span>
              </div>
              <div>
                <div class="cv-agent-name">{{ store.selectedAgent?.nombre }}</div>
                <div class="cv-agent-role">{{ currentCV?.rol }}</div>
              </div>
            </div>
            <span class="material-icons cv-close-btn" @click="showInfo = false">close</span>
          </div>

          <!-- Modal Body -->
          <div class="cv-body" v-if="currentCV">
            <p class="cv-intro">{{ currentCV.intro }}</p>

            <!-- Capabilities sections -->
            <div v-for="section in currentCV.secciones" :key="section.titulo" class="cv-section">
              <div class="cv-section-title">
                <span class="material-icons cv-section-icon" :style="{ color: store.selectedAgent?.color }">
                  {{ section.icono }}
                </span>
                {{ section.titulo }}
              </div>
              <ul class="cv-list">
                <li v-for="item in section.items" :key="item">{{ item }}</li>
              </ul>
            </div>

            <!-- What it cannot do -->
            <div class="cv-section cv-section-limits">
              <div class="cv-section-title">
                <span class="material-icons cv-section-icon" style="color:#e53935">block</span>
                Fuera de mi alcance
              </div>
              <ul class="cv-list cv-list-limits">
                <li v-for="item in currentCV.noPuede" :key="item">{{ item }}</li>
              </ul>
            </div>
          </div>

          <div class="cv-footer">
            <span class="material-icons" style="font-size:14px;opacity:.5">lock</span>
            <span>Recuerda que soy un agente IA y puedo cometer errores.</span>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Messages -->
    <div class="chat-messages" ref="messagesRef">
      <div v-if="store.messages.length === 0" class="chat-welcome">
        <span class="material-icons chat-welcome-icon" :style="{ color: store.selectedAgent?.color }">
          {{ store.selectedAgent?.icono }}
        </span>
        <p>Hola <strong>{{ store.userName }}</strong>, soy <strong>{{ store.selectedAgent?.nombre }}</strong>.</p>
        <p class="chat-welcome-sub">{{ welcomeMessage }}</p>
        <div class="quick-prompts">
          <div
            v-for="prompt in quickPrompts"
            :key="prompt"
            class="quick-prompt-chip"
            @click="sendQuick(prompt)"
          >{{ prompt }}</div>
        </div>
      </div>

      <div
        v-for="(msg, i) in store.messages"
        :key="i"
        :class="['chat-message', msg.role === 'user' ? 'msg-user' : 'msg-assistant']"
      >
        <div v-if="msg.role === 'assistant'" class="msg-avatar" :style="{ background: store.selectedAgent?.color }">
          <span class="material-icons" style="font-size:16px">{{ store.selectedAgent?.icono }}</span>
        </div>
        <div class="msg-bubble-wrapper">
          <div
            class="msg-bubble"
            :class="{ 'msg-bubble-error': msg.isError }"
            v-html="formatMessage(msg.content)"
          ></div>
          <!-- Error detail toggle -->
          <div v-if="msg.isError && msg.errorDetail" class="error-detail-wrapper">
            <button class="error-detail-toggle" @click="toggleErrorDetail(i)">
              <span class="material-icons">{{ expandedErrors.has(i) ? 'expand_less' : 'expand_more' }}</span>
              {{ expandedErrors.has(i) ? 'Ocultar detalles' : 'Ver detalles técnicos' }}
            </button>
            <div v-if="expandedErrors.has(i)" class="error-detail-box">{{ msg.errorDetail }}</div>
          </div>
          <div v-if="msg.role === 'assistant'" class="msg-actions">
            <button
              class="copy-btn"
              :class="{ copied: copiedIndex === i }"
              @click="copyMessage(msg.content, i)"
              title="Copiar respuesta"
            >
              <span class="material-icons">{{ copiedIndex === i ? 'check' : 'content_copy' }}</span>
            </button>
            <template v-if="msg.logId">
              <button
                class="rate-btn"
                :class="{ active: msg.rating === 1 }"
                title="Buena respuesta"
                @click="rate(msg, 1)"
              >
                <span class="material-icons">thumb_up</span>
              </button>
              <button
                class="rate-btn"
                :class="{ active: msg.rating === -1 }"
                title="Mala respuesta"
                @click="rate(msg, -1)"
              >
                <span class="material-icons">thumb_down</span>
              </button>
            </template>
          </div>
        </div>
      </div>

      <div v-if="store.loading" class="chat-message msg-assistant">
        <div class="msg-avatar" :style="{ background: store.selectedAgent?.color }">
          <span class="material-icons" style="font-size:16px">{{ store.selectedAgent?.icono }}</span>
        </div>
        <div class="msg-bubble-wrapper">
          <div class="msg-bubble typing-indicator">
            <span></span><span></span><span></span>
          </div>
        </div>
      </div>
    </div>

    <!-- Input -->
    <div class="chat-input-area">
      <textarea
        ref="inputRef"
        v-model="inputText"
        class="chat-input"
        placeholder="Escribe tu pregunta..."
        rows="1"
        :disabled="store.loading"
        @keydown.enter.exact.prevent="send"
        @input="autoResize"
      ></textarea>
      <button
        class="chat-send-btn"
        :disabled="!inputText.trim() || store.loading"
        :style="{ background: store.selectedAgent?.color }"
        @click="send"
      >
        <span class="material-icons">send</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch } from 'vue';
import { useAgentStore, type ChatMessage } from '../stores/agent.store';

const store = useAgentStore();
const inputText   = ref('');
const messagesRef = ref<HTMLElement | null>(null);
const inputRef    = ref<HTMLTextAreaElement | null>(null);
const isExpanded  = ref(false);
const copiedIndex = ref<number | null>(null);
const showInfo       = ref(false);
const expandedErrors = ref<Set<number>>(new Set());

function toggleErrorDetail(index: number) {
    const next = new Set(expandedErrors.value);
    if (next.has(index)) next.delete(index);
    else next.add(index);
    expandedErrors.value = next;
}

interface AgentCV {
    rol: string;
    intro: string;
    secciones: { titulo: string; icono: string; items: string[] }[];
    noPuede: string[];
}

const agentCVs: Record<string, AgentCV> = {
    atlas: {
        rol: 'Analista de Business Intelligence',
        intro: 'ATLAS transforma los datos operacionales de tu empresa en insights accionables. Consulta métricas, analiza tendencias y entiende el rendimiento del negocio en lenguaje natural.',
        secciones: [
            {
                titulo: 'Análisis de Entregas',
                icono: 'local_shipping',
                items: [
                    'Resumen de órdenes por período: total, entregadas, no entregadas, en proceso',
                    'Tasa de éxito de entrega con comparativas por fecha',
                    'Volumen de envíos filtrado por sucursal de origen o destino',
                    'Ingresos totales generados en el período consultado',
                ],
            },
            {
                titulo: 'Rendimiento de Transportistas',
                icono: 'people',
                items: [
                    'Ranking de transportistas por % efectividad de entrega',
                    'Gestionadas, entregadas y no entregadas por conductor',
                    'Comparativa entre transportistas en un mismo período',
                ],
            },
            {
                titulo: 'Geografía y Comunas',
                icono: 'map',
                items: [
                    'Comunas con mayor concentración de envíos',
                    'Tasa de éxito de entrega por zona geográfica',
                    'Identificación de comunas problemáticas (baja efectividad)',
                ],
            },
            {
                titulo: 'Clientes y Campañas',
                icono: 'business',
                items: [
                    'Ranking de clientes por volumen de órdenes e ingresos generados',
                    'Tasa de entrega exitosa por cliente/mandante',
                    'Rendimiento de campañas específicas: volumen y efectividad',
                    'Rentabilidad por tipo de bulto o especie (sobre, caja, pallet, etc.)',
                ],
            },
            {
                titulo: 'SLA y Tiempos de Proceso',
                icono: 'timer',
                items: [
                    'Tiempo promedio de retiro (desde creación hasta recepción en bodega)',
                    'Tiempo promedio de tránsito (bodega a asignación de ruta)',
                    'Tiempo promedio de entrega (ruta a entrega efectiva)',
                    'SLA total de la cadena completa de distribución',
                ],
            },
        ],
        noPuede: [
            'Generar o exportar archivos (Excel, PDF, CSV)',
            'Crear gráficos o visualizaciones visuales',
            'Modificar, crear o eliminar órdenes o registros',
            'Consultar el estado en tiempo real de rutas activas (eso es NEXUS)',
            'Redactar comunicaciones para clientes finales (eso es ARIA)',
            'Realizar análisis financieros de márgenes y costos (eso es HERMES)',
            'Inventar datos: si no encuentra información, lo indicará claramente',
        ],
    },

    nexus: {
        rol: 'Agente de Torre de Control Operacional',
        intro: 'NEXUS es los ojos de la operación en tiempo real. Consulta el estado de órdenes individuales, monitorea rutas activas y detecta problemas antes de que escalen.',
        secciones: [
            {
                titulo: 'Consulta de Órdenes',
                icono: 'search',
                items: [
                    'Estado actual de una orden por número de guía o ID',
                    'Historial completo de eventos de una orden (bitácora)',
                    'Datos del destinatario: nombre, dirección y comuna de entrega',
                    'Fechas clave: creación, recepción en bodega, asignación a ruta, entrega',
                    'Cliente/mandante asociado a la orden',
                ],
            },
            {
                titulo: 'Monitoreo de Operaciones',
                icono: 'radar',
                items: [
                    'Órdenes pendientes de retiro en un período o sucursal',
                    'Órdenes en tránsito o en ruta actualmente',
                    'Órdenes no entregadas y su contexto operacional',
                    'Identificación de órdenes retrasadas según SLA',
                    'Rutas activas: tipo (retiro/entrega) y estado',
                ],
            },
            {
                titulo: 'Búsqueda y Filtros',
                icono: 'filter_list',
                items: [
                    'Búsqueda de órdenes por RUT o nombre del destinatario',
                    'Filtro por cliente/mandante y período',
                    'Órdenes recepcionadas en bodega pendientes de despacho',
                    'Consulta de múltiples órdenes de un mismo remitente o cliente',
                ],
            },
        ],
        noPuede: [
            'Modificar el estado de una orden ni reasignarla',
            'Crear rutas o asignar operadores',
            'Enviar notificaciones o correos a clientes finales',
            'Acceder a información financiera o de ingresos (eso es HERMES)',
            'Realizar análisis estadísticos o de tendencias (eso es ATLAS)',
            'Generar reportes exportables ni archivos adjuntos',
            'Inventar datos: si no encuentra una orden, lo dirá directamente',
        ],
    },

    hermes: {
        rol: 'Analista Financiero y de Rentabilidad',
        intro: 'HERMES piensa como un CFO. Analiza los ingresos, detecta dónde se pierde dinero, evalúa el rendimiento financiero por cliente y sucursal, y sugiere oportunidades de optimización.',
        secciones: [
            {
                titulo: 'Ingresos y Facturación',
                icono: 'attach_money',
                items: [
                    'Resumen de ingresos brutos en cualquier período',
                    'Desglose: ingresos de órdenes entregadas vs no entregadas',
                    'Ingresos por cliente/mandante con ranking y tasa de éxito',
                    'Ingresos por sucursal de origen o destino',
                    'Desglose de cargos: tarifa troncal, cargo de retiro, cargo de entrega a domicilio',
                ],
            },
            {
                titulo: 'Impacto y Riesgo Financiero',
                icono: 'trending_down',
                items: [
                    'Monto económico comprometido en órdenes no entregadas',
                    'Porcentaje del total de ingresos en riesgo por no entrega',
                    'Análisis de órdenes pendientes de cobro o conciliación',
                    'Comparativa entre ingresos teóricos y realizados',
                ],
            },
            {
                titulo: 'Tarifas y Convenios',
                icono: 'receipt_long',
                items: [
                    'Análisis de tarifas base del sistema por ruta y tipo de servicio',
                    'Convenios especiales por cliente: descuentos y condiciones',
                    'Recargos por zona de entrega domiciliaria',
                    'Condiciones comerciales y límites de crédito por mandante',
                ],
            },
        ],
        noPuede: [
            'Generar facturas, notas de crédito o documentos tributarios',
            'Modificar tarifas, convenios o condiciones comerciales',
            'Exportar reportes en Excel, PDF u otros formatos',
            'Realizar proyecciones o forecasting financiero (estimaciones a futuro)',
            'Consultar costos de APIs externas (módulo no disponible en esta instalación)',
            'Acceder a datos contables externos al sistema Dribling',
            'Inventar cifras: todos los datos provienen de la base de datos real',
        ],
    },

    aria: {
        rol: 'Agente de Atención al Cliente',
        intro: 'ARIA es el puente entre tu operación y tus clientes. Consulta el estado de envíos, redacta respuestas profesionales y ayuda a comunicar cualquier novedad con empatía y claridad.',
        secciones: [
            {
                titulo: 'Consulta de Envíos para Clientes',
                icono: 'inventory_2',
                items: [
                    'Estado actual de un envío por número de guía',
                    'Última novedad registrada y fecha estimada de gestión',
                    'Dirección de destino y datos del destinatario',
                    'Información de sucursales y puntos de retiro disponibles',
                ],
            },
            {
                titulo: 'Redacción de Comunicaciones',
                icono: 'edit_note',
                items: [
                    'Respuestas profesionales para correo electrónico o WhatsApp',
                    'Mensajes de disculpa por demoras o incidentes de entrega',
                    'Comunicados informativos sobre el estado de un pedido',
                    'Textos listos para copiar y pegar, en tono empático y claro',
                    'Adaptación del mensaje al canal: formal (email) o cercano (chat)',
                ],
            },
            {
                titulo: 'Soporte al Equipo de Atención',
                icono: 'support_agent',
                items: [
                    'Explicar situaciones de entrega fallida de forma comprensible para el cliente',
                    'Sugerir alternativas concretas frente a una no entrega',
                    'Información de contacto y horarios de sucursales',
                    'Orientación sobre próximos pasos en el proceso logístico',
                ],
            },
        ],
        noPuede: [
            'Modificar, reasignar o cancelar órdenes de entrega',
            'Enviar mensajes directamente a los clientes (solo redacta, no envía)',
            'Acceder a información financiera o de facturación del negocio',
            'Realizar análisis estadísticos o de rendimiento operacional',
            'Gestionar reclamos formales ni emitir resoluciones',
            'Generar archivos adjuntos, imágenes o documentos',
            'Inventar el estado de un envío: solo informa lo que está registrado',
        ],
    },
};

const currentCV = computed(() => agentCVs[store.selectedAgent?.key ?? ''] ?? null);

const quickPromptsMap: Record<string, string[]> = {
    atlas:  ['¿Cuál fue la tasa de entrega este mes?', '¿Qué transportista tiene mejor rendimiento?', 'Muéstrame las comunas con más entregas'],
    nexus:  ['¿Hay órdenes retrasadas hoy?', '¿Qué operadores están activos?', 'Busca la orden con ID...'],
    hermes: ['¿Cuáles son los ingresos del mes?', '¿Qué mandante genera más ingresos?', '¿Cuáles son los ingresos del mes por sucursal?'],
    aria:   ['Consulta el estado de la orden...', 'Redacta una respuesta de disculpa por retraso', 'Busca las órdenes del cliente...'],
};

const welcomeMessages: Record<string, string> = {
    atlas:  '¿En qué datos o métricas te puedo ayudar hoy?',
    nexus:  '¿Qué operación quieres monitorear?',
    hermes: '¿Qué análisis financiero necesitas?',
    aria:   '¿Con qué consulta de cliente te ayudo?',
};

const quickPrompts  = computed(() => quickPromptsMap[store.selectedAgent?.key ?? ''] ?? []);
const welcomeMessage = computed(() => welcomeMessages[store.selectedAgent?.key ?? ''] ?? '¿En qué te puedo ayudar?');

async function send() {
    const text = inputText.value.trim();
    if (!text || store.loading) return;
    inputText.value = '';
    if (inputRef.value) inputRef.value.style.height = 'auto';
    await store.sendMessage(text);
    scrollToBottom();
    nextTick(() => inputRef.value?.focus());
}

function sendQuick(prompt: string) {
    inputText.value = prompt;
    nextTick(() => inputRef.value?.focus());
}

function changeAgent() {
    store.clearAgent();
}

function autoResize(e: Event) {
    const el = e.target as HTMLTextAreaElement;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
}

function scrollToBottom() {
    nextTick(() => {
        if (messagesRef.value) {
            messagesRef.value.scrollTop = messagesRef.value.scrollHeight;
        }
    });
}

async function rate(msg: ChatMessage, value: 1 | -1) {
    if (!msg.logId || msg.rating !== null) return;

    const idx      = store.messages.indexOf(msg);
    const prevUser = store.messages.slice(0, idx).reverse().find(m => m.role === 'user');
    const pregunta  = prevUser?.content ?? '';
    const respuesta = msg.content;

    await store.rateMessage(msg.logId, value, pregunta, respuesta);
}

// ── Clipboard ────────────────────────────────────────────────────────────────

async function copyMessage(content: string, index: number) {
    const agentColor = store.selectedAgent?.color ?? '#37474f';
    const htmlContent = formatMessageForClipboard(content, agentColor);

    let success = false;

    if (navigator.clipboard) {
        try {
            await navigator.clipboard.write([
                new ClipboardItem({
                    'text/html':  new Blob([htmlContent], { type: 'text/html' }),
                    'text/plain': new Blob([content],     { type: 'text/plain' }),
                }),
            ]);
            success = true;
        } catch {
            try { await navigator.clipboard.writeText(content); success = true; } catch { }
        }
    }

    if (!success) {
        // Fallback para contextos no seguros (HTTP)
        const ta = document.createElement('textarea');
        ta.value = content;
        ta.style.cssText = 'position:fixed;opacity:0;top:0;left:0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        try { success = document.execCommand('copy'); } catch { }
        document.body.removeChild(ta);
    }

    if (success) {
        copiedIndex.value = index;
        setTimeout(() => { copiedIndex.value = null; }, 1800);
    }
}

/**
 * Converts markdown content to fully inline-styled HTML for email clients.
 * Tables get proper border/background so they survive paste into Outlook.
 */
function formatMessageForClipboard(text: string, accentColor: string): string {
    const TABLE_RE = /(\|[^\n]+\|\n\|[-| :]+\|\n(?:\|[^\n]+\|\n?)+)/g;

    const thStyle = [
        `background:${accentColor}`,
        'color:#ffffff',
        'padding:8px 12px',
        'text-align:left',
        'font-weight:600',
        'white-space:nowrap',
        'border:1px solid rgba(0,0,0,0.15)',
        'font-family:Arial,sans-serif',
        'font-size:13px',
    ].join(';');

    const tdStyle = [
        'padding:7px 12px',
        'border:1px solid #e0e0e0',
        'color:#333333',
        'font-family:Arial,sans-serif',
        'font-size:13px',
    ].join(';');

    const tdAltStyle = tdStyle + ';background:#f5f5f5';

    text = text.replace(TABLE_RE, (match) => {
        const lines = match.trim().split('\n').filter(l => l.trim());
        if (lines.length < 3) return match;

        const parseRow = (line: string) =>
            line.split('|').map(c => c.trim()).filter((_, i, arr) => i > 0 && i < arr.length - 1);

        const headers = parseRow(lines[0]);
        const rows    = lines.slice(2).map(parseRow);

        const thead = `<thead><tr>${headers.map(h =>
            `<th style="${thStyle}">${h}</th>`).join('')}</tr></thead>`;

        const tbody = `<tbody>${rows.map((r, ri) =>
            `<tr>${r.map(c =>
                `<td style="${ri % 2 === 0 ? tdStyle : tdAltStyle}">${c}</td>`
            ).join('')}</tr>`
        ).join('')}</tbody>`;

        return `<table style="border-collapse:collapse;width:100%;margin:10px 0;">${thead}${tbody}</table>`;
    });

    return text
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/`(.+?)`/g, '<code style="background:#f0f0f0;padding:1px 5px;border-radius:3px;font-family:monospace;font-size:12px;">$1</code>')
        .replace(/#{1,3}\s(.+)/g, '<strong style="font-size:14px;">$1</strong>')
        .replace(/\n/g, '<br>');
}

// ── Chat display (screen render, uses CSS classes) ────────────────────────────

function parseMarkdownTable(match: string): string {
    const lines = match.trim().split('\n').filter(l => l.trim());
    if (lines.length < 3) return match;
    const parseRow = (line: string) =>
        line.split('|').map(c => c.trim()).filter((_, i, arr) => i > 0 && i < arr.length - 1);
    const headers = parseRow(lines[0]);
    const rows    = lines.slice(2).map(parseRow);
    const thead   = `<thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>`;
    const tbody   = `<tbody>${rows.map(r => `<tr>${r.map(c => `<td>${c}</td>`).join('')}</tr>`).join('')}</tbody>`;
    return `<div class="table-wrapper"><table class="agent-table">${thead}${tbody}</table></div>`;
}

function formatMessage(text: string): string {
    text = text.replace(/(\|[^\n]+\|\n\|[-| :]+\|\n(?:\|[^\n]+\|\n?)+)/g, parseMarkdownTable);
    return text
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/`(.+?)`/g, '<code>$1</code>')
        .replace(/#{1,3}\s(.+)/g, '<strong style="font-size:1.05em">$1</strong>')
        .replace(/\n/g, '<br>');
}

watch(() => store.messages.length, scrollToBottom);
</script>

<style scoped>
.agent-chat-panel {
  position: fixed;
  bottom: 100px;
  right: 28px;
  width: 380px;
  max-height: 560px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 9998;
  animation: slideUp 0.25s ease;
  transition: width 0.3s ease, max-height 0.3s ease;
}

.agent-chat-panel.expanded {
  width: 700px;
  max-height: 680px;
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* Header */
.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--agent-color, #11105E);
  color: white;
  flex-shrink: 0;
}

.chat-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.agent-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.2);
  border: 2px solid rgba(255,255,255,0.5);
}

.agent-avatar .material-icons {
  color: white;
  font-size: 20px;
}

.agent-name {
  font-weight: 700;
  font-size: 15px;
  line-height: 1.2;
}

.agent-desc {
  font-size: 11px;
  opacity: 0.85;
}

.chat-header-actions {
  display: flex;
  gap: 6px;
}

.chat-action-btn {
  cursor: pointer;
  opacity: 0.8;
  font-size: 20px;
  transition: opacity 0.2s;
}

.chat-action-btn:hover {
  opacity: 1;
}

/* Messages */
.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 200px;
  background: #f8f9fa;
}

.chat-welcome {
  text-align: center;
  padding: 20px 10px;
  color: #555;
}

.chat-welcome-icon {
  font-size: 48px;
  display: block;
  margin-bottom: 12px;
}

.chat-welcome p {
  margin: 4px 0;
  font-size: 14px;
}

.chat-welcome-sub {
  color: #888;
  font-size: 13px !important;
  margin-top: 8px !important;
}

.quick-prompts {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
  margin-top: 16px;
}

.quick-prompt-chip {
  background: white;
  border: 1px solid var(--agent-color, #11105E);
  color: var(--agent-color, #11105E);
  border-radius: 20px;
  padding: 5px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.quick-prompt-chip:hover {
  background: var(--agent-color, #11105E);
  color: white;
}

/* Message bubbles */
.chat-message {
  display: flex;
  gap: 8px;
  align-items: flex-end;
}

.msg-user {
  flex-direction: row-reverse;
}

.msg-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.msg-bubble-wrapper {
  display: flex;
  flex-direction: column;
  max-width: 80%;
  gap: 4px;
}

.msg-user .msg-bubble-wrapper {
  align-items: flex-end;
}

.msg-bubble {
  padding: 10px 14px;
  border-radius: 16px;
  font-size: 13px;
  line-height: 1.5;
  word-break: break-word;
}

.msg-user .msg-bubble {
  background: var(--agent-color, #11105E);
  color: white;
  border-bottom-right-radius: 4px;
}

.msg-assistant .msg-bubble {
  background: white;
  color: #333;
  border-bottom-left-radius: 4px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.1);
}

/* Error bubble */
.msg-bubble-error {
  background: #fff8f8 !important;
  border: 1px solid #ffcdd2 !important;
  color: #c62828 !important;
}

.error-detail-wrapper {
  margin-top: 2px;
}

.error-detail-toggle {
  display: flex;
  align-items: center;
  gap: 2px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 11px;
  color: #e57373;
  padding: 2px 0;
  transition: color 0.15s;
}

.error-detail-toggle:hover {
  color: #c62828;
}

.error-detail-toggle .material-icons {
  font-size: 14px;
}

.error-detail-box {
  background: #fff3f3;
  border: 1px solid #ffcdd2;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 11px;
  font-family: monospace;
  color: #b71c1c;
  word-break: break-all;
  white-space: pre-wrap;
  margin-top: 4px;
  max-height: 120px;
  overflow-y: auto;
}

/* Message action row (copy + rating) */
.msg-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  opacity: 0;
  transition: opacity 0.15s;
}

.msg-assistant:hover .msg-actions {
  opacity: 1;
}

.copy-btn,
.rate-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  color: #aaa;
  transition: color 0.15s;
}

.copy-btn:hover {
  color: var(--agent-color, #11105E);
}

.copy-btn.copied {
  color: #4CAF50;
}

.rate-btn:hover {
  color: var(--agent-color, #11105E);
}

.rate-btn.active {
  color: var(--agent-color, #11105E);
  opacity: 1;
}

.copy-btn .material-icons,
.rate-btn .material-icons {
  font-size: 14px;
}

/* Markdown tables */
.table-wrapper {
  overflow-x: auto;
  margin: 8px 0;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

/* agent-table styles must be :deep or unscoped since they're injected via v-html */

/* Typing indicator */
.typing-indicator {
  display: flex;
  gap: 5px;
  align-items: center;
  padding: 12px 16px;
}

.typing-indicator span {
  width: 7px;
  height: 7px;
  background: #aaa;
  border-radius: 50%;
  animation: bounce 1.2s infinite;
}

.typing-indicator span:nth-child(2) { animation-delay: 0.2s; }
.typing-indicator span:nth-child(3) { animation-delay: 0.4s; }

@keyframes bounce {
  0%, 80%, 100% { transform: scale(0.8); opacity: 0.5; }
  40% { transform: scale(1.2); opacity: 1; }
}

/* Input area */
.chat-input-area {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #eee;
  background: white;
  flex-shrink: 0;
}

.chat-input {
  flex: 1;
  resize: none;
  border: 1px solid #ddd;
  border-radius: 20px;
  padding: 9px 14px;
  font-size: 13px;
  outline: none;
  font-family: inherit;
  transition: border-color 0.2s;
  max-height: 120px;
  overflow-y: auto;
}

.chat-input:focus {
  border-color: var(--agent-color, #11105E);
}

.chat-send-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s, transform 0.15s;
  flex-shrink: 0;
}

.chat-send-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.chat-send-btn:not(:disabled):hover {
  transform: scale(1.08);
}

.chat-send-btn .material-icons {
  color: white;
  font-size: 18px;
}
</style>

<style scoped>
/* ── Agent CV Modal ── */
.agent-cv-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  animation: fadeInOverlay 0.2s ease;
}

@keyframes fadeInOverlay {
  from { opacity: 0; }
  to   { opacity: 1; }
}

.agent-cv-modal {
  background: #fff;
  border-radius: 20px;
  width: 100%;
  max-width: 680px;
  max-height: 86vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideInModal 0.25s ease;
}

@keyframes slideInModal {
  from { transform: translateY(24px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
}

/* CV Header */
.cv-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  color: white;
  flex-shrink: 0;
}

.cv-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.cv-avatar {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba(255,255,255,0.2);
  border: 2px solid rgba(255,255,255,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cv-avatar .material-icons {
  font-size: 26px;
  color: white;
}

.cv-agent-name {
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 1px;
  line-height: 1.1;
}

.cv-agent-role {
  font-size: 13px;
  opacity: 0.85;
  margin-top: 2px;
}

.cv-close-btn {
  cursor: pointer;
  font-size: 24px;
  opacity: 0.8;
  transition: opacity 0.15s;
}

.cv-close-btn:hover {
  opacity: 1;
}

/* CV Body */
.cv-body {
  overflow-y: auto;
  padding: 24px 28px;
  flex: 1;
}

.cv-intro {
  font-size: 14px;
  color: #444;
  line-height: 1.7;
  margin: 0 0 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f0f0f0;
}

.cv-section {
  margin-bottom: 20px;
}

.cv-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #222;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
}

.cv-section-icon {
  font-size: 18px;
}

.cv-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.cv-list li {
  font-size: 13px;
  color: #555;
  line-height: 1.5;
  padding-left: 18px;
  position: relative;
}

.cv-list li::before {
  content: '✓';
  position: absolute;
  left: 0;
  color: var(--agent-color, #11105E);
  font-weight: 700;
  font-size: 12px;
}

.cv-section-limits {
  margin-top: 4px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
}

.cv-list-limits li::before {
  content: '✕';
  color: #e53935;
}

.cv-list-limits li {
  color: #777;
}

/* CV Footer */
.cv-footer {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 28px;
  border-top: 1px solid #f0f0f0;
  font-size: 11px;
  color: #999;
  background: #fafafa;
  flex-shrink: 0;
}
</style>

<!-- Global styles for v-html injected table markup -->
<style>
.agent-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.agent-table th {
  background: var(--agent-color, #11105E);
  color: white;
  padding: 7px 10px;
  text-align: left;
  font-weight: 600;
  white-space: nowrap;
}

.agent-table td {
  padding: 6px 10px;
  border-bottom: 1px solid #f0f0f0;
  color: #333;
}

.agent-table tbody tr:nth-child(even) td {
  background: #f8f9fa;
}

.agent-table tbody tr:hover td {
  background: #eef2ff;
}
</style>
